import React, { useState, useEffect, useRef } from 'react';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DateField from '../DateField';

import { Canvas } from '@react-three/fiber';

import CelestialBody from '../../main/objects/body';
import SolarSystem from '../../main/objects/system';

import { calendarDateToString, calendarDateToTime, clamp, timeToCalendarDate } from '../../main/libs/math';
import { makeDateFields, timeFromDateFieldState } from '../../utils';

import { atom, PrimitiveAtom, useAtom } from 'jotai';
import { timeSettingsAtom } from '../../App';
import OrbitPlot from './OrbitPlot';
import DisplayOptions from './DisplayOptions';
import WarpButtons from './WarpButtons';

export type OrbitDisplayProps = {
  label:              string,
  index:              number,
  centralBody:        CelestialBody,
  system:             SolarSystem,
  flightPlans?:       FlightPlan[],
  landedVessels?:     LandedVessel[],
  startDate?:         number,
  endDate?:           number,
  slider?:            boolean,
  marks?:             {value: number, label: string}[],
  fullScreen?:        boolean,
}

interface OrbitDisplayPropsWithInfo extends OrbitDisplayProps {
  tabValue?:    number,
  infoItemAtom: PrimitiveAtom<InfoItem>,
}

interface OrbitDisplayControlsProps {
  fullScreen: boolean;
  speed: number;
  setSpeed: React.Dispatch<React.SetStateAction<number>>;
  intervalRef: React.MutableRefObject<NodeJS.Timer | null>;
  startWarp: () => void;
  stopWarp: () => void;
  slider: boolean;
  date: number;
  setDate: React.Dispatch<React.SetStateAction<number>>;
  sDate: number;
  eDate: number;
  timeSettings: TimeSettings;
  marks: {value: number, label: string}[];
  dateFieldDisabled: boolean;
  setUpdateFields: React.Dispatch<React.SetStateAction<boolean>>;
  dateFieldAtom: PrimitiveAtom<CalendarDate>;
  optsOpen: boolean;
  setOptsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function OrbitDisplayControls({
  fullScreen, speed, setSpeed, intervalRef, startWarp, stopWarp, slider, date, setDate, sDate, eDate, timeSettings, marks, dateFieldDisabled, setUpdateFields, dateFieldAtom, optsOpen, setOptsOpen
}: OrbitDisplayControlsProps) {
  const containerStyle: React.CSSProperties = {
    position: 'absolute', 
    bottom: 16, 
    left: '50%', 
    transform: 'translateX(-50%)', 
    width: fullScreen ? 'auto' : '100%',
    maxWidth: '800px',
    display: 'flex', 
    flexDirection: 'column', 
    alignItems: 'center',
    pointerEvents: 'none',
    zIndex: 10
  };

  const innerStyle: React.CSSProperties = {
    pointerEvents: 'auto', 
    width: '100%', 
    display: 'flex', 
    flexDirection: 'column', 
    alignItems: 'center'
  };

  const panelStyle: React.CSSProperties = {
    padding: '8px',
    marginBottom: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    backgroundColor: 'rgba(10, 10, 20, 0.6)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(0, 243, 255, 0.2)'
  };

  const rowStyle: React.CSSProperties = {
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    backgroundColor: 'rgba(10, 10, 20, 0.6)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(0, 243, 255, 0.2)'
  };

  return (
    <div style={containerStyle}>
      <div style={innerStyle}>
        {!fullScreen && <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1 }}>Click object for detailed info. Double-click to focus view.</Typography>}
        
        <div style={panelStyle}>
          <WarpButtons speed={speed} setSpeed={setSpeed} intervalRef={intervalRef} startWarp={startWarp} stopWarp={stopWarp} />
          { slider && <Slider
            sx={{ width: "90%", mt: 1 }}
            size="small"
            valueLabelDisplay="auto"
            value={date}
            valueLabelFormat={(d: number) => calendarDateToString(timeToCalendarDate(d, timeSettings, 1, 1))}
            min={Math.ceil(sDate)}     
            max={Math.floor(eDate)}
            step={Math.max((eDate-sDate)/1000, 1)}
            marks={marks}
            disabled={dateFieldDisabled}
            /* @ts-ignore */
            onChange={(event) => setDate(Number(event.target.value)) }
            onChangeCommitted={() => { setUpdateFields(true) }}
          /> }
        </div>

        <div style={rowStyle}>
            <DateField id={'plot-date'} label={''} calendarDateAtom={dateFieldAtom} correctFormat={true} variant="all" disabled={dateFieldDisabled}/>
            <Button 
                size="small"
                variant="text" 
                startIcon={optsOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                onClick={() => setOptsOpen(!optsOpen)}
            >
                Display
            </Button>
        </div>
        
        <Collapse in={optsOpen} timeout="auto" sx={{ width: '100%', mt: 1 }}>
          <div style={{ padding: '16px', background: 'rgba(10, 10, 20, 0.8)', border: '1px solid rgba(0, 243, 255, 0.2)' }}>
            <DisplayOptions />
          </div>
        </Collapse>
      </div>
    </div>
  );
}

function OrbitDisplay({tabValue = 0, index, centralBody, system, flightPlans=[], landedVessels=[], startDate=0, endDate=startDate + 9201600, slider=false, marks=[], infoItemAtom, fullScreen = false}: OrbitDisplayPropsWithInfo) {
  

  const [timeSettings] = useAtom(timeSettingsAtom);
  const timeSettingsRef = useRef(timeSettings);
  // If no mission/flight plans are provided, allow a wide calendar range (1980-2100)
  const MIN_YEAR = 1980;
  const MAX_YEAR = 2100;

  // helper to compute day-of-year for current UTC date
  const getCurrentCalendarFields = () => {
    const now = new Date();
    const startOfYear = Date.UTC(now.getUTCFullYear(), 0, 1);
    const day = Math.floor((Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - startOfYear) / (24 * 3600 * 1000)) + 1;
    return { year: now.getUTCFullYear(), day, hour: now.getUTCHours(), minute: now.getUTCMinutes(), second: now.getUTCSeconds() } as CalendarDate;
  }

  let derivedSDate = Number.isFinite(startDate) && !isNaN(startDate) ? startDate : (Number.isFinite(endDate) && !isNaN(endDate) ? endDate : 0);
  let derivedEDate = Number.isFinite(endDate) && !isNaN(endDate) ? endDate : (Number.isFinite(startDate) && !isNaN(startDate) ? startDate : 0);

  // when there are no flight plans (not in a mission) expose a wide datetime range
  if((!flightPlans || flightPlans.length === 0) && slider) {
    const minTime = calendarDateToTime({year: MIN_YEAR, day: 1, hour: 0, minute: 0, second: 0}, timeSettings, 1, 1);
    const maxTime = calendarDateToTime({year: MAX_YEAR, day: 1, hour: 0, minute: 0, second: 0}, timeSettings, 1, 1);
    derivedSDate = minTime;
    derivedEDate = maxTime;
  }

  const [date, setDate] = useState(() => {
    // default to current UTC calendar date mapped into simulation time when not in a mission
    if((!flightPlans || flightPlans.length === 0) && slider) {
      const nowFields = getCurrentCalendarFields();
      const nowTime = calendarDateToTime(nowFields, timeSettings, 1, 1);
      return Math.ceil(Math.min(Math.max(nowTime, derivedSDate), derivedEDate));
    }
    return Math.ceil(derivedSDate);
  });
  const dateRef = useRef(date);
  const startDateRef = useRef(startDate);
  const dateFieldAtom = useRef(atom(makeDateFields(...Object.values(timeToCalendarDate(Math.ceil(date), timeSettings, 1, 1))))).current;
  const [dateField, setDateField] = useAtom(dateFieldAtom);
  const dateFieldRef = useRef(dateField);
  const [updateFields, setUpdateFields] = useState(false);

  const [optsOpen, setOptsOpen] = useState(false);

  const [speed, setSpeed] = useState(0);
  const speedRef = useRef(speed);
  const intervalRef = useRef<null | NodeJS.Timer>(null);
  const counterRef = useRef(0);
  const frameRate = useRef(60).current;                   // per second
  const frameDuration = useRef(1000/frameRate).current;   // ms
  const realTime = useRef(Date.now());
  const [dateFieldDisabled, setDateFieldDisabled] = useState(false);

  const handleWarp = () => {
      setDateField((prevDateField) => {
        const dateNow = Date.now();
        const interval = dateNow - realTime.current;
        realTime.current = dateNow;
        counterRef.current = counterRef.current + 1;
        const prevDate = calendarDateToTime(prevDateField, timeSettings, 1, 1);
        const newDate = prevDate + speedRef.current * interval / 1000;
        const newDateField = timeToCalendarDate(newDate, timeSettings, 1, 1)
        return newDateField;
      });
  }

  const startWarp = () => {
      if (intervalRef.current) return;
      realTime.current = Date.now();
      intervalRef.current = setInterval(() => {
          handleWarp();
      }, frameDuration);
      setDateFieldDisabled(true);
  };

  const stopWarp = () => {
      if (intervalRef.current) {
          clearInterval(intervalRef.current);
      }
      intervalRef.current = null;
      counterRef.current = 0;
      setDateFieldDisabled(false);
  };

  useEffect(() => {
    if((timeSettings === timeSettingsRef.current)) {
        if(startDate !== startDateRef.current) {
          startDateRef.current = startDate;
          const newDate = Math.ceil(clamp(date, derivedSDate, derivedEDate))
          setDate(newDate);
          dateRef.current = newDate;
          const calendarDate = timeToCalendarDate(newDate, timeSettings, 1, 1);
          setDateField(calendarDate);
          dateFieldRef.current = calendarDate;
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, timeSettings]);

  useEffect(() => {
      if(dateField !== dateFieldRef.current) {
        dateFieldRef.current = dateField;
        const newDate = timeFromDateFieldState(dateField, timeSettings, 1, 1);
        setDate(newDate)
      } else {
        dateRef.current = date;
        if(updateFields || (timeSettings !== timeSettingsRef.current)) {
            timeSettingsRef.current = timeSettings;
            setUpdateFields(false);
            const calendarDate = timeToCalendarDate(date, timeSettings, 1, 1);
            setDateField(calendarDate);
            dateFieldRef.current = calendarDate;
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, dateField, updateFields, timeSettings]);

  useEffect(() => {
    speedRef.current = speed;
  }, [speed])

  useEffect(() => {
    setSpeed(0);
    stopWarp();
  }, [tabValue])
  
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <Canvas style={{height: fullScreen ? '100%' : '500px', width: '100%'}} gl={{logarithmicDepthBuffer: true}} frameloop={'always'} shadows={true} >
        <OrbitPlot 
          index={index}
          tabValue={tabValue}
          centralBody={centralBody} 
          system={system} 
          date={date} 
          flightPlans={flightPlans}
          landedVessels={landedVessels}
          infoItemAtom={infoItemAtom}
        />
      </Canvas>

      <OrbitDisplayControls 
        fullScreen={fullScreen}
        speed={speed}
        setSpeed={setSpeed}
        intervalRef={intervalRef}
        startWarp={startWarp}
        stopWarp={stopWarp}
        slider={slider}
        date={date}
        setDate={setDate}
        sDate={derivedSDate}
        eDate={derivedEDate}
        timeSettings={timeSettings}
        marks={marks}
        dateFieldDisabled={dateFieldDisabled}
        setUpdateFields={setUpdateFields}
        dateFieldAtom={dateFieldAtom}
        optsOpen={optsOpen}
        setOptsOpen={setOptsOpen}
      />
    </div>
  )
}

export default OrbitDisplay;